/**
 * Brew Mode Component
 * 使用 Swiper.js 实现的冲泡模式展示组件
 * 响应式轮播效果
 */

class BrewMode extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.defaultActiveIndex = parseInt(this.dataset.defaultActive) || 0;
    this.currentIndex = this.defaultActiveIndex;

    // 获取DOM元素
    this.tabs = null;

    // 键盘事件处理器
    this.keyboardHandler = null;

    // ResizeObserver 用于监听内容卡片高度变化
    this.resizeObserver = null;

    // MutationObserver 用于监听DOM变化 (暂未使用)
    this.mutationObserver = null;

    // 标志位：防止高度统一时触发 ResizeObserver 的循环调用
    this.isUnifyingHeight = false;

    // 当前统一的高度值
    this.currentUnifiedHeight = 0;

    // 重试计数器
    this.unifyRetryCount = 0;
  }

  connectedCallback() {
    // 初始化移动端标志位
    this.lastIsMobile = window.innerWidth < 768;

    // 初始化Tab切换
    this.initMobileTabs();

    // 初始化Swiper
    this.loadSwiper();

    // 设置 ResizeObserver 监听内容卡片高度变化
    this.setupResizeObserver();

    // 监听图片加载完成
    this.setupImageLoadListener();

    // 统一内容区域高度 - 所有设备都需要
    // 初始化时多次统一，确保准确
    setTimeout(() => {
      this.unifyContentHeight();
    }, 100);
    
    setTimeout(() => {
      this.unifyContentHeight();
    }, 500);
    
    setTimeout(() => {
      this.unifyContentHeight();
    }, 1000);

    // 监听窗口大小变化
    this.handleResize = this.debounce(() => {
      const isMobile = window.innerWidth < 768;
      const wasMobile = this.lastIsMobile !== undefined ? this.lastIsMobile : isMobile;

      if (this.swiper) {
        this.swiper.update();
      }

      // 窗口大小改变时重新统一高度
      this.unifyContentHeight();

      this.lastIsMobile = isMobile;
    }, 250);

    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
      this.keyboardHandler = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    window.removeEventListener('resize', this.handleResize);
  }

  /**
   * 初始化Tab切换
   */
  initMobileTabs() {
    this.tabs = this.querySelectorAll('.brew-mode__tab');

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.switchTab(index);
      });
    });
  }

  /**
   * 切换Tab
   */
  switchTab(index) {
    // 更新Tab状态
    this.tabs.forEach((tab, i) => {
      if (i === index) {
        tab.classList.add('brew-mode__tab--active');
      } else {
        tab.classList.remove('brew-mode__tab--active');
      }
    });

    // 如果存在Swiper，同步切换
    if (this.swiper) {
      // 在循环模式下，使用 slideToLoop 方法
      if (this.swiper.params.loop) {
        this.swiper.slideToLoop(index);
      } else {
        this.swiper.slideTo(index);
      }
    }

    this.currentIndex = index;
  }

  /**
   * 更新Tab的活动状态（由Swiper触发）
   */
  updateTabActiveState(index) {
    this.tabs.forEach((tab, i) => {
      if (i === index) {
        tab.classList.add('brew-mode__tab--active');
        // 滚动到活动的Tab
        this.scrollTabIntoView(tab);
      } else {
        tab.classList.remove('brew-mode__tab--active');
      }
    });
    this.currentIndex = index;
  }

  /**
   * 滚动Tab到可见区域（居中显示，边界除外）
   */
  scrollTabIntoView(tab) {
    const tabsContainer = this.querySelector('.brew-mode__tabs');
    if (!tabsContainer) return;

    const containerRect = tabsContainer.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    
    // 计算 tab 相对于容器的位置
    const tabRelativeLeft = tabRect.left - containerRect.left + tabsContainer.scrollLeft;
    const tabCenter = tabRelativeLeft + tabRect.width / 2;
    const containerCenter = tabsContainer.clientWidth / 2;
    
    // 目标滚动位置：让 tab 居中
    let targetScroll = tabCenter - containerCenter;
    
    // 限制滚动范围，避免超出边界
    const maxScroll = tabsContainer.scrollWidth - tabsContainer.clientWidth;
    targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));
    
    // 平滑滚动到目标位置
    tabsContainer.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }

  /**
   * 加载Swiper库
   */
  async loadSwiper() {
    // 检查 Swiper 是否已加载
    if (typeof Swiper !== 'undefined') {
      this.initSwiper();
      return;
    }

    // 加载 Swiper CSS
    if (!document.querySelector('#swiper-css')) {
      const swiperCSS = document.createElement('link');
      swiperCSS.id = 'swiper-css';
      swiperCSS.rel = 'stylesheet';
      swiperCSS.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
      document.head.appendChild(swiperCSS);
    }

    // 加载 Swiper JS
    if (!document.querySelector('#swiper-js')) {
      const swiperJS = document.createElement('script');
      swiperJS.id = 'swiper-js';
      swiperJS.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      swiperJS.onload = () => {
        this.initSwiper();
      };
      document.head.appendChild(swiperJS);
    } else {
      // 如果脚本已存在但还未加载完成，等待加载
      const checkSwiper = setInterval(() => {
        if (typeof Swiper !== 'undefined') {
          clearInterval(checkSwiper);
          this.initSwiper();
        }
      }, 100);
    }
  }

  /**
   * 初始化Swiper
   */
  initSwiper() {
    const swiperElement = this.querySelector('.brew-mode__pc-swiper');
    if (!swiperElement) return;

    const slides = swiperElement.querySelectorAll('.swiper-slide');
    if (slides.length === 0) return;

    // 获取导航按钮和分页器
    const prevButtons = this.querySelectorAll('.brew-mode__nav-button--prev');
    const nextButtons = this.querySelectorAll('.brew-mode__nav-button--next');
    const pagination = this.querySelector('.brew-mode__pagination');

    // 配置选项
    const swiperConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      speed: 600,
      initialSlide: this.defaultActiveIndex,
      // 添加淡入淡出效果
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      pagination: {
        el: pagination,
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '"></span>';
        },
      },
      on: {
        slideChange: (swiper) => {
          this.handleSlideChange(swiper);
        },
        slideChangeTransitionEnd: () => {
          // 切换完成后再次更新位置，确保精准
          this.updateNavButtonsPosition();
        },
        init: () => {
          // Swiper 初始化完成后统一内容高度和更新位置
          setTimeout(() => {
            // 所有设备都统一高度
            this.unifyContentHeight();
            // 再次统一确保准确
            setTimeout(() => {
              this.unifyContentHeight();
            }, 500);
          }, 200);
        },
      },
    };

    // 初始化 Swiper
    try {
      this.swiper = new Swiper(swiperElement, swiperConfig);
      
      // 为每个slide中的导航按钮添加事件监听
      prevButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.swiper) {
            this.swiper.slidePrev();
          }
        });
      });

      nextButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.swiper) {
            this.swiper.slideNext();
          }
        });
      });

      // 添加键盘导航
      this.keyboardHandler = (e) => {
        if (!this.swiper) return;
        
        if (e.key === 'ArrowLeft') {
          this.swiper.slidePrev();
        } else if (e.key === 'ArrowRight') {
          this.swiper.slideNext();
        }
      };
      document.addEventListener('keydown', this.keyboardHandler);
    } catch (error) {
      console.error('Failed to initialize Brew Mode Swiper:', error);
    }
  }

  /**
   * 处理幻灯片切换
   */
  handleSlideChange(swiper) {
    const realIndex = swiper.realIndex;
    this.currentIndex = realIndex;

    // 更新Tab的活动状态
    this.updateTabActiveState(realIndex);

    // 只更新导航按钮位置，不重新统一高度（高度在初始化时已统一）
    this.updateNavButtonsPosition();

    // 触发自定义事件
    const event = new CustomEvent('brew-mode:change', {
      detail: {
        activeIndex: swiper.activeIndex,
        realIndex: realIndex,
      },
    });
    this.dispatchEvent(event);
  }

  /**
   * 监听图片加载完成
   */
  setupImageLoadListener() {
    const images = this.querySelectorAll('.brew-mode__pc-image');
    let loadedCount = 0;
    const totalImages = images.length;

    if (totalImages === 0) {
      // 没有图片，也要统一高度
      setTimeout(() => {
        this.unifyContentHeight();
      }, 200);
      return;
    }

    const checkAllLoaded = () => {
      loadedCount++;
      // 当所有图片加载完成后，重新统一高度
      if (loadedCount === totalImages) {
        // 延迟一点时间确保图片渲染完成
        setTimeout(() => {
          this.unifyContentHeight();
        }, 200);
      }
    };

    images.forEach(img => {
      if (img instanceof HTMLImageElement) {
        if (img.complete && img.naturalHeight > 0) {
          // 图片已经加载完成
          checkAllLoaded();
        } else {
          // 监听图片加载完成
          img.addEventListener('load', checkAllLoaded);
          img.addEventListener('error', checkAllLoaded); // 即使加载失败也要计数
        }
      }
    });
  }

  /**
   * 设置 ResizeObserver 监听内容卡片高度变化
   */
  setupResizeObserver() {
    // 检查浏览器是否支持 ResizeObserver
    if (typeof ResizeObserver === 'undefined') return;

    // 使用防抖避免频繁触发
    let resizeTimeout = null;

    this.resizeObserver = new ResizeObserver(() => {
      // 如果正在统一高度，忽略此次调用，避免循环
      if (this.isUnifyingHeight) return;

      // 所有设备都监听
      // 清除之前的定时器
      clearTimeout(resizeTimeout);

      // 使用防抖，避免频繁触发
      resizeTimeout = setTimeout(() => {
        // 检查是否真的需要重新统一高度
        const contentCards = this.querySelectorAll('.brew-mode__content-card');
        let maxContentHeight = 0;
        
        // 找出内容实际需要的最大高度
        contentCards.forEach(card => {
          if (card instanceof HTMLElement) {
            // 临时移除固定高度，获取内容真实高度
            const currentHeight = card.style.height;
            card.style.height = 'auto';
            const actualHeight = card.offsetHeight;
            card.style.height = currentHeight;
            
            if (actualHeight > maxContentHeight) {
              maxContentHeight = actualHeight;
            }
          }
        });

        // 如果最大内容高度与当前统一高度差异超过2px，需要重新统一
        if (Math.abs(maxContentHeight - this.currentUnifiedHeight) > 2) {
          // 内容高度确实变化了，重新统一
          this.unifyContentHeight();
        } else {
          // 只是位置变化，更新位置即可
          this.updateNavButtonsPosition();
        }
      }, 150);
    });

    // 观察所有内容卡片
    const contentCards = this.querySelectorAll('.brew-mode__content-card');
    contentCards.forEach(card => {
      if (this.resizeObserver) {
        this.resizeObserver.observe(card);
      }
    });
  }

  /**
   * 更新导航按钮位置（移动端）
   */
  updateNavButtonsPosition() {
    // 只在移动端执行
    if (window.innerWidth >= 768) {
      // 清除移动端设置的 top 样式
      const navButtons = this.querySelector('.brew-mode__nav-buttons');
      if (navButtons && navButtons instanceof HTMLElement) {
        navButtons.style.top = '';
      }
      return;
    }

    const navButtons = this.querySelector('.brew-mode__nav-buttons');
    const pcCards = this.querySelector('.brew-mode__pc-cards');
    
    if (!navButtons || !pcCards) return;
    if (!(navButtons instanceof HTMLElement)) return;

    // 获取当前活动的 slide
    const activeSlide = this.querySelector('.swiper-slide-active');
    if (!activeSlide) return;

    const contentCard = activeSlide.querySelector('.brew-mode__content-card');
    if (!contentCard) return;

    // 获取 content-card 的位置和尺寸
    const cardRect = contentCard.getBoundingClientRect();
    const containerRect = pcCards.getBoundingClientRect();
    
    // 计算 nav-buttons 应该在的位置（距离 content-card 底部 40px）
    const topPosition = cardRect.bottom - containerRect.top - 40;
    
    navButtons.style.top = `${topPosition}px`;
  }

  /**
   * 统一内容卡片高度（所有设备）
   * 找出所有 brew-mode__content-card 中最高的，然后统一所有的高度
   */
  unifyContentHeight() {
    const contentCards = this.querySelectorAll('.brew-mode__content-card');
    if (contentCards.length === 0) return;

    // 设置标志位，防止 ResizeObserver 触发循环
    this.isUnifyingHeight = true;

    // 先清除所有已设置的高度，以便获取真实的内容高度
    contentCards.forEach(card => {
      if (card instanceof HTMLElement) {
        card.style.height = '';
        card.style.minHeight = '';
      }
    });

    // 使用 setTimeout 而不是 requestAnimationFrame，确保样式已经应用
    setTimeout(() => {
      let maxHeight = 0;

      // 找出最高的内容卡片
      contentCards.forEach(card => {
        if (card instanceof HTMLElement) {
          // 获取多个高度值，取最大的
          const offsetHeight = card.offsetHeight;
          const scrollHeight = card.scrollHeight;
          const clientHeight = card.clientHeight;
          
          const height = Math.max(offsetHeight, scrollHeight, clientHeight);
          
          if (height > maxHeight) {
            maxHeight = height;
          }
        }
      });

      // 如果找到有效的最大高度
      if (maxHeight > 0) {
        this.currentUnifiedHeight = maxHeight;

        // 统一设置所有卡片的高度和最小高度
        contentCards.forEach(card => {
          if (card instanceof HTMLElement) {
            card.style.height = `${maxHeight}px`;
            card.style.minHeight = `${maxHeight}px`;
          }
        });

        // 统一高度后，延迟更新导航按钮位置
        setTimeout(() => {
          this.updateNavButtonsPosition();
          // 重置标志位
          this.isUnifyingHeight = false;
          
          // 只在移动端验证高度
          if (window.innerWidth < 768) {
            setTimeout(() => {
              this.verifyUnifiedHeight();
            }, 100);
          }
        }, 50);
      } else {
        // 如果没有找到有效高度，直接重置标志位
        this.isUnifyingHeight = false;
      }
    }, 100);
  }

  /**
   * 验证高度是否统一，如果不统一则重试
   */
  verifyUnifiedHeight() {
    if (window.innerWidth >= 768) return;
    if (this.isUnifyingHeight) return;
    
    // 防止无限重试
    if (this.unifyRetryCount > 5) {
      this.unifyRetryCount = 0;
      return;
    }

    const contentCards = this.querySelectorAll('.brew-mode__content-card');
    let allSameHeight = true;
    let firstHeight = 0;

    contentCards.forEach((card, index) => {
      if (card instanceof HTMLElement) {
        const height = card.offsetHeight;
        if (index === 0) {
          firstHeight = height;
        } else if (Math.abs(height - firstHeight) > 2) {
          allSameHeight = false;
        }
      }
    });

    // 如果高度不统一，重新统一
    if (!allSameHeight && firstHeight > 0) {
      this.unifyRetryCount++;
      console.log(`Height verification failed (attempt ${this.unifyRetryCount}), retrying...`);
      setTimeout(() => {
        this.unifyContentHeight();
      }, 200);
    } else {
      this.unifyRetryCount = 0;
    }
  }

  /**
   * 防抖函数
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// 注册自定义元素
if (!customElements.get('brew-mode')) {
  customElements.define('brew-mode', BrewMode);
}
